import { useEffect, useMemo, useState } from "react";
import { useFootballContext } from "../../../../state/football-play/context";
import { usePlayersContext } from "../../../../state/players/context";

// A play is walked one leg at a time -- the rush, then the fumble, then the
// recovery, then the return -- and holds on the last leg long enough to read
// what happened before the next play starts.
export const REPLAY_LEG_MS = 900;
export const REPLAY_DWELL_MS = 1400;
// The ball changing hands is the moment worth watching, and its ring takes
// 1400ms to run out twice, so that leg holds longer than the rest.
export const REPLAY_TURNOVER_MS = 1600;

// Playback speeds, as a divisor on those two. Slower means a longer hold.
export const REPLAY_SPEEDS = [
	{ label: "Slow", value: 0.6 },
	{ label: "Normal", value: 1 },
	{ label: "Fast", value: 1.6 }
];

const spotOf = (play) => play?.contextSnapshot?.fieldPosition;

const isSpot = (yard) => yard !== null && yard !== undefined;

const playerLabel = (player) => {
	const name = [player.firstName?.[0], player.lastName]
		.filter(Boolean)
		.join(". ");

	return [player.uniform ? `#${player.uniform}` : null, name || player.lastName]
		.filter(Boolean)
		.join(" ");
};

/**
 * The players who touched the play, in the order the operator entered them.
 *
 * Fragments carry player references (`{ id, playerId, teamId }`), not names, so
 * they are resolved against the roster. The scan is generic rather than a field
 * name per play type -- passedBy, rushedBy, receivedBy, tackledBy and the rest
 * all resolve the same way, and a new fragment type needs no change here.
 * tackledBy holds an array, hence the array branch.
 */
const participantsOf = (play, playerById) => {
	const found = [];

	const add = (ref) => {
		const player = ref && playerById.get(ref.id || ref.playerId);

		if (player && !found.includes(player)) {
			found.push(player);
		}
	};

	[...(play.fragments || [])]
		.sort((a, b) => a.associatedHistoryIndex - b.associatedHistoryIndex)
		.forEach((fragment) =>
			Object.values(fragment).forEach((value) => {
				if (Array.isArray(value)) {
					value.forEach(add);
				} else if (value && typeof value === "object") {
					add(value);
				}
			})
		);

	return found.map(playerLabel);
};

// States that are bookkeeping rather than something that happened to the ball.
const PLUMBING_STATES = new Set([
	"START",
	"ADD_PLAY",
	"END_OF_PLAY",
	"NO_PENALTY_END_OF_PLAY",
	"FINALIZE",
	"STATS_COUNT",
	"PLAY_PENALTY_STATS_COUNT",
	"GAME_CONTROL",
	"GAME_START",
	"START_DRIVE",
	"START_PERIOD",
	"COIN_TOSS",
	"SWAP_TEAMS",
	"SET_TIME_AND_PERIOD",
	"SKIP_KICKOFF"
]);

// Only the states whose title-cased name reads badly on its own.
const STATE_LABELS = {
	KICK_OFF: "Kickoff",
	KICK_OFF_PENALTY: "Kickoff penalty",
	KICK_OFF_PENALTY_ACCEPTED: "Penalty accepted",
	KICK_OFF_PENALTY_DECLINED: "Penalty declined",
	CFL_KICK: "Open-field kick",
	PAT_TRY: "PAT",
	PLAY_PENALTY: "Penalty",
	PLAY_PENALTY_SECOND_PENALTY: "Second penalty",
	PLAY_PENALTY_PENALTY_ACCEPTED: "Penalty accepted",
	PLAY_PENALTY_PENALTY_DECLINED: "Penalty declined",
	PLAY_PENALTY_OFFSETTING: "Offsetting penalties",
	PLAY_PENALTY_SECOND_OFFSETTING: "Offsetting penalties",
	PLAY_PENALTY_NO_PLAY: "No play",
	UPDATE_FIELD: "Field updated",
	KNEELDOWN: "Kneel down",
	NO_RECOVERY_KICK_RETURN: "Kick return"
};

const stateLabel = (state) =>
	STATE_LABELS[state] ||
	state
		.toLowerCase()
		.replace(/_/g, " ")
		.replace(/^./, (c) => c.toUpperCase());

/**
 * What happened on the play, in order: "Pass", "Pass complete", "Fumble",
 * "Fumble recovered", "Tackle".
 *
 * Read off the fragments' own states, which is what the play actually is --
 * the FSM records every step the operator entered. Everything but the
 * bookkeeping states is kept, so a fumble and its recovery show up without
 * this needing a case per play type.
 */
const statesOf = (play) =>
	[...(play.fragments || [])]
		.sort((a, b) => a.associatedHistoryIndex - b.associatedHistoryIndex)
		.map((f) => f.associatedState)
		.filter((s) => s && !PLUMBING_STATES.has(s))
		.filter((s, i, all) => s !== all[i - 1]);

/**
 * A play's gain measured the way the field draws it: from where it was snapped
 * to where the ball came to rest. Sign is relative to the direction the team
 * with the ball is going, so a loss reads negative for either team.
 */
const gainOf = (play) => {
	const snap = spotOf(play);
	const end = play.endFieldPosition ?? snap;

	if (snap === null || snap === undefined || end === null || end === undefined) {
		return 0;
	}

	const towardsHigherYards = play.contextSnapshot?.homeTeamHasPossession;

	return towardsHigherYards ? end - snap : snap - end;
};

/**
 * Whoever had the ball on each leg: the receiver on the catch, the defender on
 * the recovery, the returner on the run back.
 *
 * Core builds fieldPath from the fragments that named a spot, in fragment
 * order, so the legs and those fragments line up one for one -- walking both
 * with a cursor pairs each leg back to the fragment it came from, and the first
 * player reference in that fragment is the one who had the ball.
 */
const carriersFor = (play, legs, playerById) => {
	const fragments = [...(play.fragments || [])].sort(
		(a, b) => a.associatedHistoryIndex - b.associatedHistoryIndex
	);
	let cursor = 0;

	return legs.map((leg) => {
		while (
			cursor < fragments.length &&
			fragments[cursor].associatedState !== leg.state
		) {
			cursor += 1;
		}

		const fragment = fragments[cursor];
		cursor += 1;

		if (!fragment) {
			return null;
		}

		const ref = Object.values(fragment)
			.flat()
			.find((v) => v && typeof v === "object" && playerById.get(v.id));
		const carrier = ref && playerById.get(ref.id);

		return carrier
			? { label: playerLabel(carrier), teamId: carrier.teamId }
			: null;
	});
};

// Scoring plays record no field position: a kick through the posts or a
// conversion is a result, not a spot, so the ball would sit on the snap with
// nothing to watch. These states get a leg drawn to where the ball actually
// went -- through the uprights for a kick, over the goal line for a conversion.
const KICK_AT_GOAL_STATES = [
	"FIELD_GOAL_GOOD",
	"FIELD_GOAL_MISSED",
	"PAT_KICK",
	"DROP_KICK",
	"DEUCE_GOOD",
	"DEUCE_MISSED"
];
const CONVERSION_STATES = [
	"PAT_COMPLETED",
	"PAT_MISSED",
	"PAT_POINTS_AWARDED",
	"PAT_POINTS_NOT_AWARDED",
	"PAT_RUN",
	"PAT_PASS"
];

/**
 * The leg a scoring play would otherwise not have. Returns null when the play
 * already moved the ball (a conversion run, say, names its own spots) or when
 * nothing on it was aimed at the goal.
 */
const scoringLeg = (
	play,
	states,
	legs,
	snap,
	{ fieldLength },
	homePossession
) => {
	// A try's own fragments can carry the snap spot without the ball ever
	// moving, so the test is whether anything moved it -- not whether spots
	// were recorded.
	const moved = legs.some(({ yard }) => yard !== snap);

	if (moved || !fieldLength) {
		return null;
	}

	const state = [...states]
		.reverse()
		.find(
			(s) => KICK_AT_GOAL_STATES.includes(s) || CONVERSION_STATES.includes(s)
		);

	if (!state) {
		return null;
	}

	// Where the kick actually went. The fragment records it as kickedTo, but
	// FieldGoalGood/Missed do not implement getFieldPosition(), so it never
	// reaches the core's fieldPath -- read it off the fragment instead of
	// inventing a target, and the drawing agrees with the play text.
	const kickedTo = (play.fragments || []).find(
		(f) => f.associatedState === state && isSpot(f.kickedTo)
	)?.kickedTo;

	// Failing that, aim at the goal the team with the ball is driving towards.
	// Picking the goal the ball was NEARER sent a kick the wrong way whenever
	// it was on the far side of midfield.
	const yard = isSpot(kickedTo)
		? kickedTo
		: (homePossession && fieldLength) || 0;

	return {
		state,
		yard,
		kickYards: Math.abs(yard - snap)
	};
};

const buildStep = (p, playerById, geometry) => {
	const snap = spotOf(p);
	const states = statesOf(p);
	const spotted = (p.fieldPath || []).filter(
		({ yard }) => yard !== null && yard !== undefined
	);
	// Where the play finished, when none of its fragments said so. A rush into
	// the end zone can leave the spot on the end of the play rather than on the
	// rush itself, and without this the ball would never leave the snap -- the
	// score would be announced with the ball still sitting on the line.
	const endYard = p.endFieldPosition ?? snap;
	const moved = spotted.some(({ yard }) => yard !== snap);
	const finishLeg =
		!moved && endYard !== null && endYard !== undefined && endYard !== snap
			? { state: states[states.length - 1] ?? null, yard: endYard }
			: null;

	// A scoring play that never moved the ball at all (a kick, a try) gets a
	// leg drawn to where it was kicked to.
	const scoring = finishLeg
		? null
		: scoringLeg(
				p,
				states,
				spotted,
				snap,
				geometry,
				p.contextSnapshot?.homeTeamHasPossession
		  );
	const legs = [...spotted, finishLeg, scoring].filter(Boolean);
	const carriers = carriersFor(p, legs, playerById);
	const snapshot = p.contextSnapshot;
	const offenseTeamId = snapshot?.homeTeamHasPossession
		? snapshot?.homeTeamId
		: snapshot?.awayTeamId;

	// The ball starts where it was snapped, then visits every spot a fragment
	// named. A leg that does not move the ball (a fumble at the spot it was
	// tackled) still gets its own beat, so the play reads step by step.
	//
	// A leg whose carrier is not on the team that snapped it is the moment the
	// ball changed hands -- a lost fumble, an interception. Read off who is
	// holding it rather than from a list of turnover states, so any way a play
	// can turn over is caught.
	const path = [
		{ state: null, yard: snap, carrier: null, teamId: offenseTeamId },
		...legs.map((leg, i) => ({
			...leg,
			// What this leg of the play was, so each arrow can name itself.
			label: stateLabel(leg.state),
			carrier: carriers[i]?.label ?? null,
			teamId: carriers[i]?.teamId ?? offenseTeamId,
			turnover:
				!!carriers[i]?.teamId &&
				carriers[i].teamId !== offenseTeamId &&
				carriers[i - 1]?.teamId !== carriers[i].teamId
		}))
	];

	return {
		id: p.id,
		path,
		// A play whose fragments never named a spot (an incompletion, a
		// penalty that wiped the play out) leaves the ball where it was
		// snapped.
		losYard: snap,
		ballYard: p.endFieldPosition ?? snap,
		firstDownYard: p.contextSnapshot?.firstDownAt,
		down: p.contextSnapshot?.down,
		period: p.contextSnapshot?.currentPeriod,
		time: p.time,
		gain: gainOf(p),
		isDriveStart: p.isDriveStart,
		isDriveEnd: p.isDriveEnd,
		homePossession: p.contextSnapshot?.homeTeamHasPossession,
		title: p.title,
		messages: p.messages || [],
		participants: participantsOf(p, playerById),
		// Raw states as well as their labels: a caller that has to reason about
		// what happened (did the ball reach an end zone as a score, or as a
		// safety?) must not match on display text.
		states,
		actions: states.map(stateLabel),
		// The play in words, one clause per fragment, straight from the core
		// stringifier -- the same sentences the play list shows. Kept as parts
		// rather than one blob so the account can be read step by step.
		// Messages carry newlines and tabs for the list's own layout.
		descriptionParts: (p.messages || [])
			.map((m) => (m || "").replace(/\s+/g, " ").trim())
			.filter(Boolean)
	};
};

/**
 * Walks the plays of one drive so a field graphic can show where the ball was
 * before and after each of them. Everything it reads is already on the stored
 * play -- `contextSnapshot` for the pre-play state, `endFieldPosition` (stamped
 * by the core's getGamePlays) for where the ball came to rest -- so replay
 * never touches the game.
 *
 * Yard coordinates are absolute (0 = home goal line ... fieldLength = away goal
 * line); which way the field is drawn is the caller's business.
 *
 * Only one component may run this hook: it owns the timer that advances the
 * walk, and a second caller would advance it twice per tick.
 */
const useDriveReplay = (geometry = {}) => {
	const { state, advanceReplay } = useFootballContext();
	const { state: playersState } = usePlayersContext();
	const driveReplay = state.driveReplay();
	const plays = state.plays();
	const roster = playersState.allPlayers();

	const playerById = useMemo(
		() => new Map(roster.map((p) => [p.id, p])),
		[roster]
	);

	const drivePlays = useMemo(() => {
		if (!driveReplay) {
			return [];
		}

		return plays
			.filter((p) => p.driveId === driveReplay.driveId)
			.sort((a, b) => a.index - b.index);
	}, [plays, driveReplay?.driveId]);

	const count = drivePlays.length;
	const playing = !!driveReplay?.playing;
	const index = Math.min(driveReplay?.index ?? 0, Math.max(count - 1, 0));

	const { fieldLength, endZoneLength } = geometry;

	const steps = useMemo(
		() =>
			drivePlays.map((p) =>
				buildStep(p, playerById, { fieldLength, endZoneLength })
			),
		[drivePlays, playerById, fieldLength, endZoneLength]
	);

	// Which leg of the current play the ball is on, and how fast it walks.
	// Ephemeral -- they belong to the animation, not to the replay the rest of
	// the app can see.
	const [legIndex, setLegIndex] = useState(0);
	const [speed, setSpeed] = useState(1);
	const legCount = steps[index]?.path.length ?? 1;

	useEffect(() => {
		setLegIndex(0);
	}, [index, driveReplay?.driveId]);

	useEffect(() => {
		if (!playing || !count) {
			return undefined;
		}

		// Walk the play leg by leg, holding longer on the leg where the ball
		// changed hands, then hold on the last one before handing over to the
		// next play.
		const onTurnover = !!steps[index]?.path[legIndex]?.turnover;
		const legMs = onTurnover ? REPLAY_TURNOVER_MS : REPLAY_LEG_MS;
		const timer =
			legIndex < legCount - 1
				? setTimeout(() => setLegIndex((leg) => leg + 1), legMs / speed)
				: setTimeout(
						() => advanceReplay(count),
						(onTurnover ? REPLAY_TURNOVER_MS : REPLAY_DWELL_MS) / speed
				  );

		return () => clearTimeout(timer);
	}, [playing, index, count, legIndex, legCount, speed, steps]);

	if (!driveReplay || !count) {
		return { active: false, steps: [], speed, setSpeed };
	}

	const step = steps[index];
	// Paused, the whole play is shown; playing, it builds up leg by leg.
	const activeLeg = playing ? Math.min(legIndex, legCount - 1) : legCount - 1;

	return {
		active: true,
		index,
		count,
		playing,
		steps,
		step,
		activeLeg,
		speed,
		setSpeed,
		ballYard: step.path[activeLeg].yard,
		// Only plays already walked count towards the running total, so the
		// tally builds with the drive rather than spoiling it.
		yardsSoFar: steps
			.slice(0, index + 1)
			.filter((s) => !s.isDriveStart)
			.reduce((sum, s) => sum + s.gain, 0),
		playsSoFar: steps.slice(0, index + 1).filter((s) => !s.isDriveStart).length
	};
};

export default useDriveReplay;
